import { useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Artikel, Steuersatz, Unternehmen } from "@kundeninfo/api-client";
import { ArtikelDialog } from "./ArtikelDialog.js";
import { renderMarkdown } from "../markdown.js";
import { quellenMarke } from "../format.js";
import { Chip } from "@kundeninfo/ui/me-design";
// Die Klassennamen bleiben `akat__` (Etappe 1, Aufgabe 2: keine Umbenennung an
// herausgelösten Bestandsteilen) — umgezogen ist in Fixrunde 1 nur die DATEI: Die Regeln
// dessen, was DIESER Baustein zeichnet, stehen jetzt hier statt im Stilblatt des Katalogs.
import { KiKnopf } from "./meHuellen.js";
import "./ArtikelDetail.css";

/**
 * Das ARTIKELDETAIL als eigener Baustein (Bereichs-Spalten, Etappe 1, Aufgabe 2).
 *
 * Herausgelöst aus der damaligen Katalog-Ansicht, damit die Katalogzeile und der
 * Artikel-BEREICH buchstäblich DASSELBE Detail zeichnen. Die Katalog-Ansicht selbst gibt es
 * seit Aufgabe 5 nicht mehr — sie ist in views/BereichArtikel.tsx aufgegangen (Objektliste
 * links, dieser Baustein rechts); ihre Adresse `/artikel` gilt unverändert weiter. Der
 * einzige Verwender ist damit heute der Bereich.
 *
 * Was der Baustein trägt: Name und Artikelnummer, die als Markdown GERENDERTE Beschreibung
 * (Dirk-Entscheid 05.09.2026), den deutschen Nettopreis je Einheit, Kategorie und
 * Steuersatznamen — und für ADMIN den Kurzweg „Ändern", der DENSELBEN Dialog öffnet wie die
 * Verwaltung (components/ArtikelDialog.tsx). Anlegen und Stilllegen bleiben allein der
 * Verwaltung vorbehalten; gewächtert ist der Schreibweg ohnehin auf dem SERVER.
 *
 * **Die Drei-Zeilen-Klemme gilt hier NICHT.** Dirks Bestandsregel lautete „die ersten drei
 * Zeilen der Beschreibung in LISTEN" — sie gehörte zur Trefferzeile, die zwanzig Artikel
 * untereinander vergleichbar halten muss. Seit dem 23.09.2026 zeigen die Listenzeilen
 * (Bereich UND Artikelstamm, Baustein `Objektzeile`) gar keine Beschreibung mehr, sondern
 * Preis und Kategorie; die Beschreibung steht allein HIER. Im vollwertigen Detail
 * gibt es nichts abzuschneiden: Es steht allein auf seiner Fläche, und eine Beschreibung,
 * die genau dort mit „…" endet, wäre unlesbar, ohne dass ein Weg zum Rest bliebe. Bis
 * Aufgabe 2 trug dieser Baustein die Klemme mit, weil er aus einer LISTENZEILE
 * herausgelöst wurde — im Bereich ist er kein Listenbestandteil mehr.
 *
 * Was er NICHT weiss: dass es eine Liste mit Filtern und Blätterung gibt. Der Artikel
 * kommt als GELADENER Satz herein (nicht als blosse Kennung — der Rahmen führt seine Liste
 * ohnehin), die Steuersätze und Unternehmen ebenso, und nach dem Speichern meldet der
 * Baustein nach oben, statt selbst neu zu lesen.
 */

/** Der EINE Schreibweg des ADMIN-Kurzwegs — mehr braucht das Detail nicht. */
export type ArtikelDetailClient = Pick<ApiClient, "updateProduct">;

const PREIS_FORMAT = new Intl.NumberFormat("de-DE",
  { style: "currency", currency: "EUR" });
const SATZ_FORMAT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

/**
 * Der Name der Zeitscheibe, auf die der Artikel zeigt. Der Artikel trägt bewusst nur die
 * Kennung (siehe openapi) — der Prozentsatz zum Belegdatum ist eine Frage an die
 * Zeitreihe, nicht an den Artikel. Zeigt ein Artikel auf eine inzwischen abgelöste
 * Scheibe, steht hier der Rückfalltext statt einer nackten UUID.
 */
export function steuersatzName(steuersaetze: Steuersatz[], id: string): string {
  const s = steuersaetze.find((x) => x.id === id);
  return s === undefined ? "unbekannter Steuersatz"
    : `${s.name} (${SATZ_FORMAT.format(s.ratePercent)} %)`;
}

export type ArtikelDetailProps = {
  client: ArtikelDetailClient;
  /** Der Artikel — fertig geladen, samt seiner `version`. */
  artikel: Artikel;
  /** Die sichtbaren Konzernunternehmen — der Dialog nennt das Unternehmen des Artikels. */
  companies: Unternehmen[];
  /** Die bekannten (aktiven) Steuersätze — für den Namen und die Auswahl im Dialog. */
  steuersaetze: Steuersatz[];
  /**
   * Zeigt den Ändern-Knopf und öffnet den Artikeldialog. Nur für ADMIN — für alle anderen
   * ist der Schreibweg mit 403 verschlossen (der Server prüft das, nicht diese Zeile); ein
   * Knopf, der nur in eine sichere Sackgasse führte, hätte hier nichts verloren.
   */
  istAdmin: boolean;
  /** Nach erfolgreichem Speichern: Der Rahmen liest neu. Ein geänderter Name verschiebt die
   *  Zeile innerhalb der Ordnung — das weiss nur der Server. */
  onGespeichert: () => void;
};

export function ArtikelDetail(p: ArtikelDetailProps): ReactElement {
  const { artikel } = p;
  /** Steht der Änderungsdialog zu diesem Artikel offen? (Nur ADMIN kommt hier hinein.) */
  const [dialogOffen, setDialogOffen] = useState(false);

  return (
    <>
      <div className="akat__eintrag-kopf">
        <span className="akat__name">{artikel.name}</span>
        <Chip>{artikel.productNumber}</Chip>
      </div>
      {/* Die Beschreibung — VOLL, ohne Klemme (siehe den Kopf dieser Datei: Die
          Drei-Zeilen-Regel gehört der Listenzeile, nicht dem Detail). GERENDERT
          (Dirk-Entscheid 05.09.2026, vorher reiner Text): `renderMarkdown` sanitisiert
          selbst und wird nie mit dem `unsicher`-Schalter gerufen. Leer heisst: gar kein
          Absatz. Ein <div> statt <p>, weil der Renderer Blockelemente liefert und ein <p>
          die nicht enthalten darf. */}
      {artikel.description !== null && artikel.description.trim() !== "" && (
        <div className="akat__beschreibung">
          {renderMarkdown(artikel.description)}
        </div>
      )}
      <span className="akat__preis">
        {PREIS_FORMAT.format(artikel.netUnitPrice)} netto je {artikel.unit}
      </span>
      <span className="akat__zusatz">
        {artikel.category ?? "ohne Kategorie"} ·{" "}
        {steuersatzName(p.steuersaetze, artikel.taxRateId)}
      </span>
      {/* Die Abrechnungsquelle als Chip — nur, wenn gesetzt (Spec „Abrechnungsquellen"). */}
      {quellenMarke(artikel.billingDataSource) !== null && (
        <Chip className="akat__quelle">{quellenMarke(artikel.billingDataSource)}</Chip>
      )}
      {p.istAdmin && (
        <div className="akat__aktionen">
          {/* Der ADMIN-Kurzweg: öffnet DENSELBEN Dialog wie die Verwaltung. Die
              Artikelnummer im zugänglichen Namen — zwanzigmal „Ändern" wäre für jemanden,
              der die Seite hört, nicht unterscheidbar. */}
          <KiKnopf className="akat__knopf"
            name={`${artikel.productNumber} ändern`}
            onClick={() => setDialogOffen(true)}>
            Ändern
          </KiKnopf>
        </div>
      )}

      {dialogOffen && (
        <ArtikelDialog
          artikel={artikel}
          companies={p.companies}
          // Der Katalog kennt nur die AKTIVEN Sätze (Bearbeiter-Leseweg) — für einen
          // Artikel auf einer abgelösten Scheibe hält der Dialog den Bestand selbst fest.
          steuersaetze={p.steuersaetze}
          vorgabe={{ companyId: artikel.companyId, taxRateId: artikel.taxRateId }}
          onSpeichern={async ({ felder }) => {
            // Hier wird ausschliesslich GEÄNDERT — der Baustein hat keinen Anlege-Weg.
            await p.client.updateProduct(artikel.id, felder);
          }}
          onGespeichert={() => {
            setDialogOffen(false);
            p.onGespeichert();
          }}
          onAbbrechen={() => setDialogOffen(false)}
        />
      )}
    </>
  );
}
